import React, { useState, useEffect, useRef } from 'react';
import {
  FaPhone, FaVideo, FaEllipsisV, FaPaperPlane, FaSmile, FaArrowLeft,
  FaCircle, FaPaperclip, FaImage, FaTimes,
  FaCrown, FaBan, FaTrash, FaCheck,
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import EmojiPicker from 'emoji-picker-react';
import api from '../../services/api';
import useSocket from './socketService'; // ← NEW: Import the custom hook
import { useNavigate } from 'react-router-dom';

const Messages = ({ conversationToOpenId = null, setActiveTab }) => {
  const navigate = useNavigate();

  const [conversations, setConversations] = useState([]);
  const [convSearch, setConvSearch] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState({});
  const [typingConversations, setTypingConversations] = useState({});
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [interestStatus, setInterestStatus] = useState(null);
  const [conversationAllowed, setConversationAllowed] = useState(false);
  const [chatAccess, setChatAccess] = useState(false);


  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimerRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  const readOnceRef = useRef(new Set());
  const suppressSoundRef = useRef(false);
  const BASE_URL = api.defaults.baseURL;
  const API_BASE = BASE_URL.replace("/api", "");
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = storedUser?.id || storedUser?._id || null;

  const getConvId = (conv) => conv?._id || conv?.conversationId || conv?.id;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  // === MARK AS READ (Only once per conversation open) ===
  const markConversationAsRead = async (conversationId) => {
    if (!conversationId || readOnceRef.current.has(conversationId)) return;

    try {
      await api.post(`/chat/conversations/${conversationId}/read`);
      readOnceRef.current.add(conversationId);

      setConversations(prev =>
        prev.map(conv =>
          getConvId(conv) === conversationId
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
    } catch (err) {
      console.error("Failed to mark conversation as read:", err);
    }
  };

  // === SOCKET HOOK ===
  const {
    isConnected: socketConnected,
    joinConversation,
    leaveConversation,
    sendMessage,
    sendTyping,
    sendStopTyping,
  } = useSocket(userId, {
    onConnect: () => {
      console.log("Socket connected");

    },
    onOnlineUsers: (userIds) => {
      const map = {};
      userIds.forEach(id => { map[String(id)] = true; });
      setOnlineUsers(map);
    },
    onUserOnline: ({ userId: uid }) => {
      setOnlineUsers(prev => ({ ...prev, [String(uid)]: true }));
    },
    onUserOffline: ({ userId: uid }) => {
      setOnlineUsers(prev => {
        const copy = { ...prev };
        delete copy[String(uid)];
        return copy;
      });
    },
    onNewMessage: ({ conversationId, message }) => {
      const convIdStr = String(conversationId);
      const activeConvStr = String(activeConversationIdRef.current);
      const isActiveConversation = convIdStr === activeConvStr;
      const isOwnMessage = String(message.sender) === String(userId);



      setConversations(prev => {
        const updated = prev.map(conv => {
          if (String(getConvId(conv)) === convIdStr) {
            return {
              ...conv,
              lastMessage: message.text || "📷 Image",
              message: message.text || "📷 Image",
              updatedAt: new Date().toISOString(),
              unreadCount: isActiveConversation ? 0 : (conv.unreadCount || 0) + 1,
            };
          }
          return conv;
        });

        const index = updated.findIndex(c => String(getConvId(c)) === convIdStr);
        if (index > -1) {
          const [conv] = updated.splice(index, 1);
          updated.unshift(conv);
        }

        return updated;
      });

      if (isActiveConversation) {
        setMessages(prev => {
          // stop duplicate from server
          if (message._id && prev.some(m => m._id && m._id === message._id)) {
            return prev;
          }

          // replace temp message
          const tempIndex = prev.findIndex(
            m =>
              typeof m._id === "string" &&
              m._id.startsWith("temp-") &&
              m.sender === message.sender &&
              m.text === message.text
          );


          if (tempIndex !== -1) {
            const updated = [...prev];
            updated[tempIndex] = message;
            return updated;
          }

          return [...prev, message];
        });

        scrollToBottom();
        setPartnerTyping(false);
      }

    },
    onInterestAccepted: async ({ conversationId }) => {
      console.log("✅ Interest accepted:", conversationId);
      const convs = await fetchConversations();
      const updated = convs.find(c => getConvId(c) === conversationId);
      if (updated) {
        activeConversationIdRef.current = conversationId;
        setSelectedConversation(updated);
        joinConversation(conversationId);
        fetchMessages(conversationId);
      }
    },
    onInterestRejected: async ({ conversationId }) => {
      console.log("❌ Interest rejected:", conversationId);
      await fetchConversations();
      if (activeConversationIdRef.current === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    },
    onInterestCancelled: async ({ conversationId }) => {
      await fetchConversations();
      if (activeConversationIdRef.current === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    },
    onTyping: ({ conversationId }) => {
      setTypingConversations(prev => ({ ...prev, [conversationId]: true }));
      if (String(getConvId(selectedConversation)) === String(conversationId)) {
        setPartnerTyping(true);
      }
    },
    onStopTyping: ({ conversationId }) => {
      setTypingConversations(prev => {
        const copy = { ...prev };
        delete copy[conversationId];
        return copy;
      });
      if (String(getConvId(selectedConversation)) === String(conversationId)) {
        setPartnerTyping(false);
      }
    },
  });

  const partnerId = (() => {
    if (!selectedConversation) return null;
    const participants = selectedConversation.participants || [];
    const other = participants.find(id => String(id) !== String(userId));
    return other
      ? String(other)
      : String(selectedConversation.otherUser?._id || selectedConversation.user?._id || null);
  })();

  const partnerOnline = partnerId ? onlineUsers[partnerId] : false;

  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      activeConversationIdRef.current = null;
      setInterestStatus(null);
      setConversationAllowed(false);
      return;
    }

    const convId = getConvId(selectedConversation);
    if (!convId) return;

    activeConversationIdRef.current = convId;

    setConversations(prev => {
      const targetConv = prev.find(c => getConvId(c) === convId);
      if (!targetConv || (targetConv.unreadCount || 0) === 0) {
        return prev;
      }
      return prev.map(conv =>
        getConvId(conv) === convId
          ? { ...conv, unreadCount: 0 }
          : conv
      );
    });

    if (socketConnected()) {
      joinConversation(convId);
    }


    fetchMessages(convId);
    markConversationAsRead(convId);
    fetchInterestStatus(convId);
  }, [selectedConversation]);

  useEffect(() => {
    fetchCurrentUser();
    fetchConversations();
  }, []);



  useEffect(() => {
    if (selectedConversation) {
      suppressSoundRef.current = true;
      const t = setTimeout(() => {
        suppressSoundRef.current = false;
      }, 800);
      return () => clearTimeout(t);
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (conversationToOpenId && conversations.length > 0) {
      const conv = conversations.find(c => getConvId(c) === conversationToOpenId);
      if (conv) {
        setSelectedConversation(conv);
      }
    }
  }, [conversationToOpenId, conversations]);

  useEffect(() => scrollToBottom(), [messages]);

  const fetchInterestStatus = async (conversationId) => {
    if (!conversationId) return;
    try {
      const res = await api.get(`/chat/get/${conversationId}/getstatus`);
      setInterestStatus(res.data.interest?.status || null);
      setConversationAllowed(res.data.conversationAllowed || false);
    } catch (err) {
      console.error("Failed to fetch interest status:", err);
      setInterestStatus(null);
      setConversationAllowed(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const res = await api.get('/dashboard/my-profile');
      setCurrentUser(res.data.profile || res.data.user);
      setUserPlan(res.data.subscription?.currentPlan || 'free');
      setChatAccess(
        res.data.subscription?.planDetails?.features?.chatAccess === true
      );
    } catch (err) {
      console.error("Failed to fetch current user:", err);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await api.get('/chat/conversations');
      const convs = res.data.conversations || res.data || [];
      setConversations(convs);
      setLoading(false);
      return convs;
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
      setLoading(false);
      return [];
    }
  };

  const fetchMessages = async (conversationId) => {
    if (!conversationId) return;
    try {
      const res = await api.get(`/chat/conversations/${conversationId}`);
      const msgs = res.data.messages || res.data.liveChatHistory || [];
      const formatted = msgs.map(m => ({
        ...m,
        _id: m._id || `msg-${Date.now()}-${Math.random()}`,
        text: m.text || null,
        image: m.image || null,
        sender: m.sender,
        time: m.time || new Date().toISOString(),
      }));
      setMessages(formatted);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setMessages([]);
    }
  };
  const getPlanLabel = (plan) => {
    if (!plan) return null;

    const map = {
      basic: "BASIC",
      prime: "PRIME",
      elite: "ELITE",
    };

    return map[plan] || plan.toUpperCase();
  };

  const getPlanStyles = (plan) => {
    switch (plan) {
      case "elite":
        return "from-yellow-400 to-orange-400";
      case "prime":
        return "from-purple-500 to-pink-500";
      case "basic":
        return "from-blue-500 to-cyan-500";
      default:
        return "from-gray-400 to-gray-500";
    }
  };

  const renderAvatar = (user, profile, size = 'w-10 h-10') => {
    const src =
      user?.profilePhoto ||
      profile?.profilePhoto ||
      profile?.photos?.[0] ||
      (profile?.basicInfo?.gender === 'Female'
        ? 'https://randomuser.me/api/portraits/women/85.jpg'
        : 'https://randomuser.me/api/portraits/men/85.jpg');

    const fallback = 'https://randomuser.me/api/portraits/men/85.jpg';

    if (!src) return <img src={fallback} className={`${size} rounded-full object-cover`} alt="avatar" />;

    if (src.startsWith('data:image') || src.startsWith('http')) {
      return <img src={src} alt="Avatar" className={`${size} rounded-full object-cover border`} />;
    }

    return (
      <img
        src={`${API_BASE}${src}`}
        alt="Avatar"
        className={`${size} rounded-full object-cover border`}
        onError={(e) => (e.target.src = fallback)}
      />
    );
  };

  const isPremiumPlan = (plan) => ['basic', 'prime', 'elite'].includes(plan);

  const handleTyping = () => {
    if (!selectedConversation || !socketConnected()) return;
    if (!chatAccess) return;
    const convId = getConvId(selectedConversation);
    sendTyping(convId);

    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      sendStopTyping(convId);
    }, 1000);
  };

  const handleSendMessage = () => {
    if (!messageText.trim() || !selectedConversation || !socketConnected()) return;

    const convId = getConvId(selectedConversation);

    setConversations(prev => {
      const updated = prev.map(conv => {
        if (String(getConvId(conv)) === String(convId)) {
          return {
            ...conv,
            lastMessage: messageText,
            message: messageText,
            updatedAt: new Date().toISOString(),
            unreadCount: 0,
          };
        }
        return conv;
      });

      const index = updated.findIndex(c => String(getConvId(c)) === String(convId));
      if (index > -1) {
        const [conv] = updated.splice(index, 1);
        updated.unshift(conv);
      }
      return updated;
    });

    const tempId = `temp-${Date.now()}`;
    setMessages(prev => [...prev, {
      _id: tempId,
      text: messageText,
      sender: userId,
      time: new Date().toISOString(),
    }]);

    sendMessage(convId, messageText, null);

    setMessageText("");
    setShowEmojiPicker(false);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file || !selectedConversation || !socketConnected()) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const tempId = `temp-img-${Date.now()}`;
      setMessages(prev => [...prev, {
        _id: tempId,
        text: null,
        image: ev.target.result,
        sender: userId,
        time: new Date().toISOString(),
      }]);
      scrollToBottom();

      sendMessage(getConvId(selectedConversation), null, ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAcceptInterest = async () => {
    try {
      await api.post('/chat/interest/accept', { conversationId: getConvId(selectedConversation) });
      const convs = await fetchConversations();
      const updated = convs.find(c => getConvId(c) === getConvId(selectedConversation));
      if (updated) {
        setSelectedConversation(updated);
        fetchMessages(getConvId(updated));
      }
    } catch (err) {
      console.error("Accept interest failed:", err);
    }
  };

  const handleRejectInterest = async () => {
    try {
      await api.post('/chat/interest/reject', { conversationId: getConvId(selectedConversation) });
      await fetchConversations();
      setSelectedConversation(null);
    } catch (err) {
      console.error("Reject interest failed:", err);
    }
  };

  const handleBlockConversation = () => {
    setConfirmModal({
      open: true,
      title: "Block User",
      message: "Are you sure you want to block this user? You won’t receive messages from them.",
      onConfirm: async () => {
        try {
          await api.post('/chat/block', { conversationId: getConvId(selectedConversation) });
          await fetchConversations();
          setSelectedConversation(null);
        } catch (err) {
          console.error(err);
        } finally {
          setConfirmModal({ open: false });
        }
      },
    });
  };

  const handleUnblockConversation = () => {
    setConfirmModal({
      open: true,
      title: "Unblock User",
      message: "Do you want to unblock this user and resume chatting?",
      onConfirm: async () => {
        try {
          await api.post('/chat/unblock', { conversationId: getConvId(selectedConversation) });
          const convs = await fetchConversations();
          const updated = convs.find(c => getConvId(c) === getConvId(selectedConversation));
          if (updated) setSelectedConversation(updated);
        } catch (err) {
          console.error(err);
        } finally {
          setConfirmModal({ open: false });
        }
      },
    });
  };

  const handleUpgradeClick = () => {
    if (setActiveTab) {
      setActiveTab('subscription');
    }
  };

  const handleCancelInterest = async () => {
    try {
      await api.post('/chat/interest/cancel', { conversationId: getConvId(selectedConversation) });
      await fetchConversations();
      setSelectedConversation(null);
      setMessages([]);
    } catch (err) {
      console.error(err);
    }
  };

  const leaveConversationFunc = () => {
    const convId = getConvId(selectedConversation);

    if (convId && socketConnected()) {
      leaveConversation(convId);
    }

    window.dispatchEvent(
      new CustomEvent("active-conversation", { detail: null })
    );

    setSelectedConversation(null);
    setMessages([]);
    setPartnerTyping(false);
    setIsChatOpen(false);
  };




  const isPending = interestStatus === 'pending';
  const interestSentByMe = isPending && selectedConversation?.interest?.sentBy === userId;
  const interestReceived = isPending && !interestSentByMe;
  const isBlocked = selectedConversation?.isBlocked || false;
  const blockedByMe = selectedConversation?.isBlockedByMe || false;
  const shouldLockChat = chatAccess === false


  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        <p className="mt-4 text-text-secondary">Loading messages...</p>
      </div>
    );
  }


  return (
    <>
      <div className="w-full mx-auto h-[calc(100vh-120px)] grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 lg:p-6">
        {/* Conversations List */}
        <div className={`lg:col-span-1 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-2xl shadow-sm border border-glass-border overflow-hidden flex flex-col
    ${isChatOpen ? 'hidden lg:flex' : 'flex'}`}>
          <div className="p-6 border-b border-glass-border">
            <h2 className="text-2xl font-bold text-text-primary">Messages</h2>
            <div className="mt-3 relative">
              <input
                type="text"
                placeholder="Search conversations..."
                value={convSearch}
                onChange={(e) => setConvSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[rgba(255,255,255,0.05)] border-none rounded-full focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
              <FaSmile className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-text-muted">
                <p className="text-sm">No conversations yet</p>
                <p className="text-xs mt-2">Send an interest to start chatting</p>
              </div>
            ) : (
              conversations
                .filter(conv => {
                  if (!convSearch) return true;
                  const q = convSearch.toLowerCase();
                  const name = (conv.user?.name || conv.otherUser?.name || '').toLowerCase();
                  const msg = (conv.message || conv.lastMessage || '').toLowerCase();
                  return name.includes(q) || msg.includes(q);
                })
                .map(conv => (
                  <motion.button
                    key={getConvId(conv)}
                    onClick={() => {
                      const convId = getConvId(conv);
                      activeConversationIdRef.current = convId;

                      // 🔥 SYNC WITH DASHBOARD
                      window.dispatchEvent(
                        new CustomEvent("active-conversation", { detail: convId })
                      );

                      if (socketConnected()) {
                        joinConversation(convId);
                      }

                      suppressSoundRef.current = true;
                      setIsChatOpen(true);
                      setSelectedConversation(conv);
                    }}

                    className={`w-full p-4 border-b border-glass-border text-left hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.05)] flex items-center justify-between transition-colors ${getConvId(selectedConversation) === getConvId(conv) ? 'bg-purple-50 dark:bg-[rgba(147,51,234,0.15)] border-l-4 border-purple-600' : ''
                      }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="relative">
                        {renderAvatar(conv.user || conv.otherUser, conv.otherUser, 'w-12 h-12')}
                        {isPremiumPlan((conv.user || conv.otherUser)?.subscriptionPlan) && (
                          <FaCrown className="absolute -top-1 -right-1 text-yellow-500 text-xs bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-full p-0.5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-text-primary truncate">
                            {(conv.user || conv.otherUser)?.name || 'Unknown'}
                          </h3>
                          {(conv.user || conv.otherUser)?.subscriptionPlan && (
                            <span
                              className={`text-xs bg-gradient-to-r ${getPlanStyles(
                                (conv.user || conv.otherUser)?.subscriptionPlan
                              )
                                } text-white px-2 py-0.5 rounded-full font-bold`}
                            >
                              {getPlanLabel(
                                (conv.user || conv.otherUser)?.subscriptionPlan
                              )}
                            </span>
                          )}

                        </div>
                        <p className="text-xs text-text-muted truncate">
                          {typingConversations[getConvId(conv)] ? (
                            <span className="text-purple-600 italic">typing...</span>
                          ) : (
                            conv.message || conv.lastMessage || 'No messages'
                          )}
                        </p>
                        {conv.interest?.status === 'pending' && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded mt-1 inline-block">Pending</span>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-[10px] text-text-muted">
                          {conv.updatedAt && new Date(conv.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {conv.unreadCount > 0 && (
                          <span className="bg-purple-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.button>
                ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`lg:col-span-2 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-2xl shadow-sm border border-glass-border overflow-hidden flex flex-col
    ${isChatOpen ? 'flex' : 'hidden lg:flex'}`}>
          {selectedConversation ? (
            <>
              <div className="p-6 border-b border-glass-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={leaveConversationFunc} className="lg:hidden text-text-secondary hover:text-text-primary">
                    <FaArrowLeft size={20} />
                  </button>
                  <div className="relative">
                    {renderAvatar(selectedConversation.user || selectedConversation.otherUser, selectedConversation.otherUser, 'w-12 h-12')}
                    {isPremiumPlan((selectedConversation.user || selectedConversation.otherUser)?.subscriptionPlan) && (
                      <FaCrown className="absolute -top-1 -right-1 text-yellow-500 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-full p-0.5" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-text-primary">
                        {(selectedConversation.user || selectedConversation.otherUser)?.name || 'Unknown'}
                      </h3>
                      {(selectedConversation.user || selectedConversation.otherUser)?.subscriptionPlan && (
                        <span
                          className={`text-xs bg-gradient-to-r ${getPlanStyles(
                            (selectedConversation.user || selectedConversation.otherUser)
                              ?.subscriptionPlan
                          )
                            } text-white px-2 py-0.5 rounded-full font-bold`}
                        >
                          {getPlanLabel(
                            (selectedConversation.user || selectedConversation.otherUser)
                              ?.subscriptionPlan
                          )}
                        </span>
                      )}


                    </div>
                    <div className="flex items-center gap-2 text-xs text-text-muted">
                      <FaCircle className={`text-xs ${partnerOnline ? 'text-green-500' : 'text-text-muted'}`} />
                      {partnerOnline ? 'Active now' : 'Offline'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative">
                    <button
                      onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                      className="p-2 hover:bg-[rgba(255,255,255,0.05)] rounded-full"
                    >
                      <FaEllipsisV className="text-text-secondary" />
                    </button>
                    {showOptionsMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-lg border border-glass-border z-50">
                        {!blockedByMe ? (
                          <button
                            onClick={() => { handleBlockConversation(); setShowOptionsMenu(false); }}
                            className="w-full px-4 py-3 text-left hover:bg-red-50 text-red-600 flex items-center gap-2"
                          >
                            <FaBan /> Block User
                          </button>
                        ) : (
                          <button
                            onClick={() => { handleUnblockConversation(); setShowOptionsMenu(false); }}
                            className="w-full px-4 py-3 text-left hover:bg-green-50 text-green-600 flex items-center gap-2"
                          >
                            <FaCheck /> Unblock User
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>


              {shouldLockChat && (
                <div className="flex-1 flex items-center justify-center bg-[rgba(255,255,255,0.03)] p-8">
                  <div className="text-center max-w-md">
                    <div className="mb-8">
                      <div className="w-28 h-28 mx-auto bg-gradient-to-br from-purple-200 to-pink-200 rounded-full flex items-center justify-center shadow-lg">
                        <FaCrown className="text-5xl text-purple-700" />
                      </div>
                    </div>

                    <h3 className="text-2xl font-bold text-text-primary mb-4">
                      Upgrade to continue chatting
                    </h3>

                    <p className="text-text-secondary mb-8 leading-relaxed">
                      Your current plan does not allow messaging with this user.
                      <br />
                      Upgrade Plan to send messages.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={handleUpgradeClick}
                        className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-full hover:shadow-xl transform hover:scale-105 transition text-lg"
                      >
                        Unlock Chat
                      </button>

                      <button
                        onClick={handleRejectInterest}
                        className="px-8 py-4 border border-glass-border text-text-secondary rounded-full hover:bg-[rgba(255,255,255,0.03)] font-medium"
                      >
                        Reject & Close
                      </button>
                    </div>

                    <p className="text-xs text-text-muted mt-6">
                      Premium members can chat without limits
                    </p>
                  </div>
                </div>
              )}

              {(interestReceived || interestSentByMe) && (
                <div className="bg-yellow-50 border-b border-yellow-200 p-4">
                  {interestReceived && (
                    <>
                      <p className="text-sm text-yellow-800 mb-3">
                        Interest request from {(selectedConversation.user || selectedConversation.otherUser)?.name}
                      </p>
                      <div className="flex gap-2">
                        <button onClick={handleAcceptInterest} className="flex-1 bg-green-500 text-white py-2 rounded-lg font-bold hover:bg-green-600">
                          Accept
                        </button>
                        <button onClick={handleRejectInterest} className="flex-1 bg-red-500 text-white py-2 rounded-lg font-bold hover:bg-red-600">
                          Reject
                        </button>
                      </div>
                    </>
                  )}
                  {interestSentByMe && (
                    <>
                      <p className="text-sm text-yellow-800 mb-2">Waiting for them to accept your interest request</p>
                      <button onClick={handleCancelInterest} className="w-full bg-red-100 text-red-600 py-2 rounded-lg font-bold hover:bg-red-200">
                        Cancel Request
                      </button>
                    </>
                  )}
                </div>
              )}


              {/* ================= MESSAGES AREA ================= */}
              {!shouldLockChat && (
                <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[rgba(255,255,255,0.03)]">
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-text-muted">
                      No messages yet. Say hi!
                    </div>
                  ) : (
                    messages.map((msg) => {
                      // System message
                      if (msg.sender === "system") {
                        return (
                          <div key={msg._id} className="flex justify-center my-3">
                            <span className="text-xs text-text-muted bg-[rgba(255,255,255,0.08)] px-4 py-1 rounded-full">
                              {msg.text}
                            </span>
                          </div>
                        );
                      }

                      const isOwn = String(msg.sender) === String(userId);

                      return (
                        <motion.div
                          key={msg._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex items-end gap-2 mb-4 ${isOwn ? "justify-end" : "justify-start"
                            }`}
                        >
                          {/* Avatar (left side) */}
                          {!isOwn &&
                            renderAvatar(
                              selectedConversation.user || selectedConversation.otherUser,
                              selectedConversation.otherUser,
                              "w-8 h-8"
                            )}

                          {/* Message bubble */}
                          <div className={`max-w-xs ${isOwn ? "order-1" : "order-2"}`}>
                            <div
                              className={`relative px-4 py-2 rounded-2xl ${isOwn
                                  ? "bg-purple-600 text-white rounded-br-none"
                                  : "bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-bl-none text-text-primary dark:text-white"
                                }`}
                            >
                              {msg.text && <p className="text-sm">{msg.text}</p>}

                              {msg.image && (
                                <img
                                  src={msg.image}
                                  alt="sent"
                                  className="rounded-lg max-w-full h-auto mt-2"
                                />
                              )}

                              <div
                                className={`text-xs mt-1 ${isOwn ? "text-purple-200" : "text-text-muted"
                                  }`}
                              >
                                {new Date(msg.time).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </div>
                          </div>

                          {/* Avatar (right side) */}
                          {isOwn &&
                            renderAvatar(currentUser, currentUser, "w-8 h-8")}
                        </motion.div>
                      );
                    })
                  )}

                  {/* Typing indicator */}
                  {partnerTyping && (
                    <div className="flex items-center gap-1 ml-2 mb-2">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}

              {conversationAllowed && chatAccess && !isBlocked && (
                <div className="relative p-3 sm:p-6 border-t border-glass-border">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="relative">
                      <button onClick={() => setShowAttachMenu(!showAttachMenu)} className="p-2 hover:bg-[rgba(255,255,255,0.05)] rounded-full text-text-secondary">
                        <FaPaperclip className="text-xl" />
                      </button>
                      {showAttachMenu && (
                        <div className="absolute bottom-full left-0 mb-2 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-lg border p-2 w-48 z-20">
                          <button
                            onClick={() => { fileInputRef.current.click(); setShowAttachMenu(false); }}
                            className="w-full px-4 py-2 text-left hover:bg-[rgba(255,255,255,0.05)] flex items-center gap-2"
                          >
                            <FaImage className="text-purple-600" /> Image
                          </button>
                        </div>
                      )}
                    </div>

                    <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-2 hover:bg-[rgba(255,255,255,0.05)] rounded-full text-text-secondary">
                      <FaSmile className="text-xl" />
                    </button>

                    <input
                      type="text"
                      value={messageText}
                      onChange={(e) => { setMessageText(e.target.value); handleTyping(); }}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 min-w-0 px-4 py-2 bg-transparent dark:bg-[rgba(255,255,255,0.03)] text-text-primary dark:text-white placeholder-text-muted border border-glass-border rounded-full focus:outline-none focus:ring-2 focus:ring-purple-600 transition-colors"
                      disabled={!socketConnected()}

                    />

                    <button
                      onClick={handleSendMessage}
                      disabled={!messageText.trim() || !socketConnected()}
                      className="p-2 h-10 w-10 flex-shrink-0 flex items-center justify-center bg-purple-600 text-white rounded-full hover:bg-purple-700 disabled:opacity-50"
                    >

                      <FaPaperPlane />
                    </button>
                  </div>

                  {showEmojiPicker && (
                    <div className="absolute bottom-20 left-0 z-50">
                      <EmojiPicker onEmojiClick={(e) => setMessageText(prev => prev + e.emoji)} />
                    </div>
                  )}

                  <input type="file" ref={fileInputRef} className="hidden" onChange={handleImageSelect} accept="image/*" />

                  {!socketConnected() && (
                    <div className="text-center text-xs text-red-500 mt-2">
                      Connecting to server...
                    </div>
                  )}

                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-text-muted text-center">
              <div>
                <p className="text-lg font-bold">Select a conversation to start chatting</p>
                <p className="text-sm mt-2">Or send an interest to someone to begin</p>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Modal */}
        {confirmModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-2xl shadow-xl w-full max-w-sm p-6"
            >
              <h3 className="text-lg font-bold text-text-primary mb-2">{confirmModal.title}</h3>
              <p className="text-sm text-text-secondary mb-6">{confirmModal.message}</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmModal({ open: false })}
                  className="px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] text-text-secondary hover:bg-[rgba(255,255,255,0.08)]"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </>
  );
};

export default Messages;